import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faCalendarDay, faCalendarWeek } from '@fortawesome/free-solid-svg-icons';
import VehicleIcon from '../vehicleIcons';
import { Colors, DesignTokens } from '../../styles/colors';
import { CAR_CARD_PRICE_TAB_LABELS, CarCardPriceMode } from '../../utils/carCardPrice';

const FilterOuter = styled.div`
  ${tw`
    w-full
    max-w-full
    min-w-0
    mx-auto
    overflow-hidden
    box-border
  `};
  max-width: ${DesignTokens.container.maxWidth};
`;

const FilterContainer = styled.div`
  ${tw`
    w-full
    max-w-full
    min-w-0
    flex
    items-center
    overflow-x-auto
    overscroll-x-contain
    py-4
    px-2
    md:px-4
    lg:px-0
    space-x-3
    box-border
  `};
  /* Hide scrollbar but allow scrolling */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`;

const FilterItem = styled.button<{ $active?: boolean }>`
  ${tw`
    flex
    items-center
    justify-center
    space-x-2
    px-4
    py-2
    rounded-lg
    cursor-pointer
    transition-all
    duration-200
    whitespace-nowrap
    flex-shrink-0
    font-semibold
  `};

  font-size: ${DesignTokens.typography.fontSize.bodySm};
  line-height: ${DesignTokens.typography.lineHeight.body};
  border: 0;
  background: ${({ $active }) => ($active ? Colors.Gold : Colors.White)};
  color: ${({ $active }) => ($active ? 'var(--color-on-primary)' : Colors.TextPrimary)};
  will-change: transform;

  &:hover {
    background: ${Colors.GoldGrey};
    color: ${Colors.Gold};
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover {
      transform: none;
    }
  }
`;

const IconWrapper = styled.div`
  ${tw`flex items-center justify-center`};
`;

const Divider = styled.span`
  ${tw`flex-shrink-0 self-stretch`};
  width: 1px;
  margin-top: 6px;
  margin-bottom: 6px;
  background: var(--color-border);
`;

/** Car types after the price tabs (spec 022 D13); "All" only where there are no price tabs. */
const ALL_CATEGORY = { id: 'all', label: 'All', type: 'SEDAN' };
const categories = [
  { id: 'suv', label: 'SUV', type: 'SUV' },
  { id: 'sedan', label: 'Sedan', type: 'SEDAN' },
  { id: 'truck', label: 'Truck', type: 'TRUCK' },
  { id: 'convertible', label: 'Convertible', type: 'CONVERTIBLE' },
];

const PRICE_TAB_ICONS: Record<CarCardPriceMode, typeof faCalendarWeek> = {
  daily: faCalendarDay,
  weekly: faCalendarWeek,
  monthly: faCalendar,
};

interface ICategoryFilterProps {
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  /**
   * Spec 022 D13, spec 028: Daily, Weekly and Monthly in the brand's order, in place of "All";
   * only the card price changes. Leave empty (host home) for the "All" chip.
   */
  priceTabs?: CarCardPriceMode[];
  priceMode?: CarCardPriceMode;
  onPriceModeChange?: (mode: CarCardPriceMode) => void;
}

export function CategoryFilter(props: ICategoryFilterProps) {
  const {
    selectedCategory,
    onCategoryChange,
    priceTabs = [],
    priceMode,
    onPriceModeChange,
  } = props;
  const withPriceTabs = priceTabs.length > 0;
  const typeChips = withPriceTabs ? categories : [ALL_CATEGORY, ...categories];
  const iconColor = (active: boolean) =>
    active ? 'var(--color-on-primary)' : 'var(--color-text-primary)';

  return (
    <FilterOuter>
      <FilterContainer>
        {priceTabs.map(mode => {
          const active = priceMode === mode;
          return (
            <FilterItem
              key={mode}
              type="button"
              $active={active}
              aria-pressed={active}
              data-testid={`home-price-tab-${mode}`}
              onClick={() => {
                // Weekly and Daily replace "All": they show every car type.
                onPriceModeChange?.(mode);
                onCategoryChange('all');
              }}
            >
              <IconWrapper>
                <FontAwesomeIcon icon={PRICE_TAB_ICONS[mode]} color={iconColor(active)} />
              </IconWrapper>
              <span>{CAR_CARD_PRICE_TAB_LABELS[mode]}</span>
            </FilterItem>
          );
        })}
        {withPriceTabs && <Divider aria-hidden />}
        {typeChips.map(category => {
          const active = selectedCategory === category.id;
          return (
            <FilterItem
              key={category.id}
              type="button"
              $active={active}
              aria-pressed={active}
              data-testid={`home-category-${category.id}`}
              onClick={() =>
                // With price tabs a second tap on a car type goes back to every type.
                onCategoryChange(withPriceTabs && active ? 'all' : category.id)
              }
            >
              <IconWrapper>
                <VehicleIcon type={category.type} size={20} color={iconColor(active)} />
              </IconWrapper>
              <span>{category.label}</span>
            </FilterItem>
          );
        })}
      </FilterContainer>
    </FilterOuter>
  );
}
